import styles from './styles.module.scss';
import classNames from 'classnames';
import { FilterIcon, Search, Cross } from 'components/Icons';
import { useCallback, useEffect, useState } from 'react';
import { Dropdown, Checkbox } from 'antd';
import { Icons } from 'pages/Workshop/Icons';
import { Button, Avatar } from 'components';
import { AutoComplete, Option, Select } from 'components-antd';
import { caseInsensitiveFilter, convertNameToAvatarPlaceholder, showErrorMessage } from 'helpers';
import { v4 as uuid } from 'uuid';
import { Icons as ProfileIcons } from 'pages/Profile/components/icons';
import { useDispatch, useSelector } from 'react-redux';
import { getActiveTeamList } from 'store/selectors/teamList';
import { setClientConnectionsFilter } from 'store/actions/app';
import { getIsClientDrawerSelector } from 'store/selectors/app';

type ClientFiltersPropsType = {
  clientTags: any[];
};

const initialFilters = {
  selectedTile: 'All Clients',
  tags: [],
  agentId: undefined,
};

const ClientFilters = (props: ClientFiltersPropsType) => {
  const { clientTags } = props;
  const dispatch = useDispatch();
  const activeTeamList = useSelector(getActiveTeamList);
  const [internalTags, setInternalTags] = useState<any[]>([]);
  const [useTags, setUsedTags] = useState<{ Id: number; TagName: string; uuid: string }[]>([]);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [inputVal, setInputVal] = useState<string>('');
  const [filters, setFilters] = useState<any>(initialFilters);
  const isDrawerOpen = useSelector(getIsClientDrawerSelector);

  useEffect(() => {
    const tagsWithUuid = clientTags.map((tag) => ({ ...tag, uuid: uuid() }));
    setInternalTags(tagsWithUuid);
  }, [clientTags]);

  useEffect(() => {
    if (!isDrawerOpen) dispatch(setClientConnectionsFilter(initialFilters));
  }, [isDrawerOpen]);

  const resetFiltersHandler = () => {
    dispatch(setClientConnectionsFilter(initialFilters));
    setFilters(initialFilters);
    setUsedTags([]);
    setShowFilters(false);
  };

  const handleFilters = () => {
    dispatch(setClientConnectionsFilter(filters));
    setShowFilters(false);
  };

  const removeTag = (uuid) => {
    setFilters((prev) => ({
      ...prev,
      tags: [...prev.tags.filter((tag) => tag.uuid !== uuid)],
    }));
    const exists = useTags.find((tag) => tag.uuid === uuid);
    if (exists) setUsedTags((prev) => [...prev.filter((tag) => tag.uuid !== uuid)]);
  };

  const handleSelect = (value) => {
    if (!value) return;

    const found = internalTags.find((tag) => tag.Id === value);
    const exists = filters.tags.find((tag) => tag.value && tag.value === found?.Id);

    if (found && !exists) {
      setFilters((prev) => ({
        ...prev,
        tags: [
          ...prev.tags,
          {
            name: found.TagName,
            value: found.Id,
            uuid: found.uuid,
            checked: true,
          },
        ],
      }));
      setUsedTags((prev) => [...prev, found]);
    } else {
      showErrorMessage('Tag already selected.');
    }

    setInputVal('');
  };

  const getFinalList = useCallback((): { Id: number; TagName: string; uuid: string }[] => {
    const finalList: { Id: number; TagName: string; uuid: string }[] = [];

    internalTags.forEach((tag) => {
      const found = useTags.find(
        (usedTag) =>
          usedTag.Id === tag.Id && usedTag.uuid === tag.uuid && usedTag.TagName === tag.TagName,
      );
      if (!found) finalList.push(tag);
    });

    const sortedList = finalList.sort((a: { TagName: string }, b: { TagName: string }) =>
      a.TagName.localeCompare(b.TagName),
    );

    return sortedList;
  }, [internalTags, useTags]);

  const handleCheckboxChange = (e, uuid) => {
    const isChecked = e.target.checked;
    setFilters((prev) => ({
      ...prev,
      tags: prev.tags.map((tag) => {
        if (tag.uuid !== uuid) return tag;

        return { ...tag, checked: isChecked };
      }),
    }));
  };

  const onSelectChange = (value) => {
    setFilters((prev) => ({ ...prev, agentId: value }));
  };

  const filtersMenu = (
    <div className={styles.filtersModal}>
      <div className={styles.filterContent}>
        <div className={styles.heading}>Filters</div>
        <div
          className={classNames(styles.tiles, {
            [styles.tilesBorder]: filters.selectedTile !== 'Agent',
          })}
        >
          <div
            className={classNames(styles.tile, {
              [styles.tileSelected]: filters.selectedTile === 'All Clients',
            })}
            onClick={() =>
              setFilters((prev) => ({ ...prev, selectedTile: 'All Clients', agentId: undefined }))
            }
          >
            All Clients
          </div>
          <div
            className={classNames(styles.tile, {
              [styles.tileSelected]: filters.selectedTile === 'My Clients',
            })}
            onClick={() =>
              setFilters((prev) => ({ ...prev, selectedTile: 'My Clients', agentId: undefined }))
            }
          >
            My Clients
          </div>
          <div
            className={classNames(styles.tile, {
              [styles.tileSelected]: filters.selectedTile === 'Agent',
            })}
            onClick={() =>
              setFilters((prev) => ({ ...prev, selectedTile: 'Agent', agentId: undefined }))
            }
          >
            Agent
          </div>
        </div>
        {filters.selectedTile === 'Agent' ? (
          <>
            <Select
              size={'large'}
              popupClassName={styles.agentSelectorPopup}
              onChange={onSelectChange}
              dropdownIconColor={'#262626'}
              suffixIcon={<ProfileIcons variant={ProfileIcons.DROPDOWN} />}
              listHeight={400}
              className={styles.agentSelector}
              value={filters.agentId}
              placeholder="Select agent"
            >
              {activeTeamList?.map((agent) => (
                <Option key={agent.id} value={agent.id}>
                  <div className={styles.agent}>
                    <Avatar
                      className={styles.avatarWrap}
                      avatarClassName={styles.avatar}
                      src={agent.avatarUrl}
                      placeholder={convertNameToAvatarPlaceholder(
                        `${agent.firstName}${agent.lastName ? ` ${agent.lastName}` : ''}`,
                      )}
                    />
                    <div className={styles.name}>{`${agent.firstName}${
                      agent.lastName ? ` ${agent.lastName}` : ''
                    }`}</div>
                  </div>
                </Option>
              ))}
            </Select>
            <div className={styles.borderDiv}></div>
          </>
        ) : null}
        <div className={styles.clientTagsInput}>
          <Search />
          <AutoComplete
            onChange={(val) => setInputVal(val)}
            popupClassName={classNames(styles.autoCompleteTagDropdown)}
            placeholder={'Search by tags...'}
            onSelect={handleSelect}
            className={styles.tagsAutoComplete}
            value={inputVal}
            filterOption={(inputValue, option) =>
              caseInsensitiveFilter(inputValue, option?.label as string)
            }
          >
            {getFinalList().length ? (
              getFinalList().map((tag) => (
                <Option key={tag.Id} label={tag.TagName} value={tag.Id}>
                  <div className={classNames(styles.autoCompleteOption)}>
                    <div className={classNames(styles.name)}>{tag.TagName}</div>
                  </div>
                </Option>
              ))
            ) : (
              <Option label={'No tag Found'} value={''}>
                <div className={classNames(styles.autoCompleteOption)}>
                  <div className={styles.noResultsText}>No results found.</div>
                </div>
              </Option>
            )}
          </AutoComplete>
        </div>
        {filters.tags.length ? (
          <div className={styles.tags}>
            {filters.tags.map((tag) => (
              <div className={styles.tag}>
                <div className={styles.left}>
                  <Checkbox
                    type="checkbox"
                    name="isPrimary"
                    className="mosaikCheckbox"
                    value={tag.checked}
                    onChange={(e) => handleCheckboxChange(e, tag.uuid)}
                    checked={tag.checked}
                  />
                  <div className={styles.name}>{tag.name}</div>
                </div>
                <div
                  className={styles.right}
                  onClick={() => {
                    removeTag(tag.uuid);
                  }}
                >
                  <Cross color="#747475" variant="small" />
                </div>
              </div>
            ))}
          </div>
        ) : null}
        <div className={styles.footer}>
          <div className={styles.reset} onClick={resetFiltersHandler}>
            <Icons variant={Icons.RESET} />
            <span>Reset All</span>
          </div>
          <Button type="submit" className={styles.done} title={'Done'} onClick={handleFilters} />
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles.clientFilters}>
      <Dropdown
        overlay={filtersMenu}
        trigger={['click']}
        open={showFilters}
        onOpenChange={(flag: boolean) => {
          setShowFilters(flag);
        }}
        className={classNames(styles.clientFiltersDD, {
          [styles.clientFiltersDDActive]: showFilters,
        })}
      >
        <div>
          <FilterIcon color={'#262626'} />
        </div>
      </Dropdown>
    </div>
  );
};

export default ClientFilters;
